import compose from './compose'
import fullLanguages from '../config/languages.json'
import LanguageDetect from 'languagedetect'
import siteLanguages from '../config/sitelanguages.json'
import { mammothAsync, readFileAsync, sleep } from './async-functions'
import { appendFilters, appendLimits, getResource } from './rest-functions'
import config from '../config'
import * as XLSX from 'xlsx'
import { asBlob } from './html-docx'
import { CheckCircleOutline, Close, Error, FiberManualRecordRounded } from '@material-ui/icons'
import { NavLink } from 'react-router-dom'
import { Menu } from '@material-ui/icons'
import { Button } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import { toast } from 'react-toastify'

export function log (...args) {
  // disable console.log in production!
  if (process.env.REACT_APP_CONSOLE_LOG !== 'false') {
    console.log(args)
  }
  return null
}

export function getValue (arr, value, defaultValue = 0) {
  return (arr === undefined || arr[value] === undefined)
    ? defaultValue
    : arr[value]
}

export function parseErrorObject (error) {
  try {
    error = JSON.parse(error.message)
  } catch (e) {
    error = { message: error.toString() }
  }

  return error
}

/**
 * convert from error object to translated message
 * @param translate the translation function from: const { t } = useTranslation();
 * @param error the object returned by backend
 * @param replacements this value is for backward compatability with an old format
 * @param parse
 * @returns {string|*} translated error
 */
export function convertFromErrorObject (translate, error, replacements = {}, parse = true) {
  if (parse) {
    error = parseErrorObject(error)
  }

  // if message does not exist
  if (error['message'] === undefined)
    return ''

  const foundedValues = [...error['message'].matchAll(/{{([\w_]+)}}/g)]

  // if we have some values inside the message
  if (foundedValues.length > 0) {
    for (const val of foundedValues) {
      if (error[val[1]] !== undefined)
        replacements[val[1]] = error[val[1]]
      else
        replacements[val[1]] = ''
    }
  }

  return translate(error['message'].replaceAll(':', ''), {
    ...replacements,
    'ret_val': translate(replacements['ret_val'])
  })
}

export function returnValuesFilter (ResultData, returnValues) {
  for (let el of Object.keys(ResultData)) {
    if (isObjectEmpty(returnValues[el]) || !returnValues[el]?.settings?.archive) {
      delete ResultData[el]
    }
  }
  return ResultData
}

export function showStateAsText (state, translate) {
  switch (state) {
    case 0:
      return translate('Pending')
    case 1:
      return translate('Approved')
    case 2:
      return translate('Disapproved')
    default:
      return state
  }
}

export function getScale (localPercent, localTarget, localScaleFrom, localScaleTo) {
  let scalePercent = localPercent
  let targetPercent = localTarget
  if (localScaleFrom !== 0 || localScaleTo !== 100) {
    const rangeScale = localScaleFrom < localScaleTo ? localScaleTo - localScaleFrom : localScaleFrom - localScaleTo
    if (localScaleFrom < localScaleTo) {
      scalePercent = localPercent > localScaleTo ? localScaleTo : localPercent < localScaleFrom ? localScaleFrom : localPercent
      targetPercent = localTarget > localScaleTo ? localScaleTo : localTarget < localScaleFrom ? localScaleFrom : localTarget
      scalePercent = (scalePercent - localScaleFrom) / (rangeScale / 100)
      targetPercent = (targetPercent - localScaleFrom) / (rangeScale / 100)
    } else {
      scalePercent = localPercent < localScaleTo ? localScaleTo : localPercent > localScaleFrom ? localScaleFrom : localPercent
      targetPercent = localTarget < localScaleTo ? localScaleTo : localTarget > localScaleFrom ? localScaleFrom : localTarget
      scalePercent = (rangeScale - (scalePercent - localScaleTo)) / (rangeScale / 100)
      targetPercent = (rangeScale - (targetPercent - localScaleTo)) / (rangeScale / 100)
    }
  }
  return [scalePercent, targetPercent]
}

export function checkClixNotZero (settings) {
  const findForCLIXHere = (val) => {
    const retArray = []
    for (const listValue of Object.keys(val)) {
      if (val[listValue]?.clix && val[listValue].clix !== 0) {
        retArray.push(listValue)
      }
    }
    return retArray
  }

  const valuesClix = []
  let dataTermsClix = []
  let dataCorporateLanguageClix = []

  if (typeof settings === 'object') {
    for (const value of Object.keys(settings)) {
      // if starting with "list"
      if (value.startsWith('list')) {
        // dataTerms
        if (value === 'listCorporateLanguageTerms') {
          dataCorporateLanguageClix = findForCLIXHere(settings[value].values['_list'])
        }
        if (value === 'listTerms') {
          dataTermsClix = findForCLIXHere(settings[value].values['_list'])
        }
        continue
      }
      // for the other type of values
      if (settings[value]?.values?.clix && settings[value].values.clix !== 0) {
        valuesClix.push(value)
      }
    }
  }
  return { valuesClix, dataCorporateLanguageClix, dataTermsClix }
}

export function deprecatedBenchmarkCheck (benchmarks, resultList) {
  if (!!benchmarks && !!resultList?.results?.benchmarkId) {
    for (let el of Object.keys(resultList?.results?.benchmarkId)) {
      if (!Object.keys(benchmarks).includes(el)) {
        return false
      }
    }
  }
  return true
}

export function setFunctionsByReturnValueType (retVal, name, summarizingType) {
  if (summarizingType === 'sum') {
    if (retVal[name].value_type === 'array') {
      return 'arraysizesum'
    }
    return 'sum'
  } else if (summarizingType === 'mean') {
    if (retVal[name].value_type === 'integer' || retVal[name].value_type === 'number') {
      return 'mean'
    }
  }
  return ''
}

export function chooseColor (value, targetYellowGreen, targetYellowRed) {
  if (targetYellowRed > targetYellowGreen)
    return (value < targetYellowGreen ? 'green' :
      value > targetYellowRed ? 'red' : 'yellow')
  return (value > targetYellowGreen ? 'green' :
    value < targetYellowRed ? 'red' : 'yellow')
}

export function findFirstDiff (str1, str2, len = 20) {
  const finded = [...str1].findIndex((el, index) => el !== str2[index])
  return [str1.substr(finded - len, len * 2), str2.substr(finded - len, len * 2)]
}

export function getListByColor (data, color) {
  for (let i of data) {
    if (i['category_type'] === color)
      return i
  }
  return null
}

export function convertJSONToObject (jsonString, defaultValue = {}) {
  if (typeof jsonString === 'object')
    return jsonString
  try {
    return JSON.parse(jsonString)
  } catch (e) {
    return defaultValue
  }
}

export function convertJSONToArray (jsonString, defaultValue = []) {
  if (Array.isArray(jsonString))
    return jsonString
  try {
    return JSON.parse(jsonString)
  } catch (e) {
    return defaultValue
  }
}

export function roundWithPrecision (num, precision = 0, string = false) {
  if (!isNaN(parseFloat(num))) {
    if (string) {
      return (+(num).toFixed(precision)).toLocaleString()
    } else {
      return +(num).toFixed(precision)
    }
  } else {
    return 0
  }
}

export async function convertTextToTT (token, tlService, rawWords, locale_name) {

  let words = []
  let lemma = []
  let tag = []

  let res

  try {
    res = await tlService.getTreeTager(token, locale_name, rawWords)
  } catch (e) {
    console.log(e)
    return false
  }
  if (res && res['dataTokenizer']) {
    for (let data of res['dataTokenizer']) {
      words.push(data.word)
      lemma.push(data.lemma)
      tag.push(data.tag)
    }
    return { words, lemma, tag }
  }
  return false
}

export function isObjectEmpty (obj) {
  if (typeof obj !== 'object')
    return obj === undefined

  // noinspection LoopStatementThatDoesntLoopJS
  for (const x in obj) {
    return false
  }
  return true
}

export function getDataFromModals (props, index) {
  const { modalData: { modal } } = props
  return modal[index] ? modal[index].data : false
}

export function getModalFromModals (props, index) {
  const { modalData: { modal } } = props
  return modal[index] ? modal[index] : false
}

export function createDefaultMultilangObject (values = {}) {
  return Object.keys(siteLanguages).reduce((prev, current) => {
    return { ...prev, [current]: (values && values[current]) ? values[current] : '' }
  }, {})
}

export function deepCopy (el) {
  return typeof el === 'object' ? JSON.parse(JSON.stringify(el)) : el
}

export function isObjectsEqual (obj1, obj2) {
  return (typeof obj1 === 'object' && typeof obj2 === 'object') ? JSON.stringify(obj1) === JSON.stringify(obj2) : obj1 === obj2
}

export function onlyUnique (value, index, self) {
  return self.indexOf(value) === index
}

export function removeEmptyFromObject (object) {
  Object.entries(object).map(([key, value]) => {
    if (key.length === 0 || (typeof value === 'string' && value.length === 0)) {
      delete object[key]
    }
    return false
  })
  return object
}

export function convertLanguagesToFull (language, hyphen = false) {
  if (language) {
    if (language.length === 5) return hyphen ? language.replace(/_/, '-') : language
    const langRegexp = new RegExp('^' + language + '\\_\\w{2}$')
    const langArray = Object.keys(fullLanguages)
    const langIndex = langArray.findIndex((element) => element.match(langRegexp))
    if (langIndex > -1) {
      return hyphen ? langArray[langIndex].replace(/_/, '-') : langArray[langIndex]
    }
  }
  return false
}

export function convertLanguagesToShort (language) {
  if (language) {
    if (language.length === 2) return language
    if (fullLanguages[language] !== undefined) {
      return fullLanguages[language]['shortName']
    }
  }
  return false
}

/*
* examples:
* checkUserRight(user,[101,103],[2,2])
* company/over company rights should always be before group right (see examples below)
* checkUserRight(user,[201,101],[0,2])
* checkUserRight(user,[308,201,101,101],[0,0,2,1])
*  */
export function checkUserRight (user, right = [], user_group = []) {
  if (user.user_type_id === 1)
    return true
  if (user?.user_rights?.length) {
    for (const tmpRight of user.user_rights) {
      const foundedIndex = right.indexOf(tmpRight.right_id)
      // forund user right in right array
      if (foundedIndex > -1) {
        // if user group id present and > 0
        if (user_group.length > foundedIndex && user_group[foundedIndex] > 0) {
          return user_group[foundedIndex] === user.user_rights[foundedIndex].user_group_id
        } else {
          // if not - approve
          return true
        }
      }
    }
  }
  return false
}

export function checkGroupRight (user, targetUser, companyLevelRight, groupLevelRight) {
  const hasBaseEditRights = checkUserRight(user, [companyLevelRight])
  if (hasBaseEditRights) {
    return true
  }
  return user?.user_rights?.find(r => r.right_id === groupLevelRight && r.user_group_id === targetUser.user_group_id)
}

export function stripHtml (html, stripList = []) {
  if (stripList?.length) {
    return stripList.reduce((acc, tag) => acc
        .replace(new RegExp(`<\\/*${tag}[^>]*>`, 'g'), '')
      , html)
  } else {
    const tmp = document.createElement('DIV')
    tmp.innerHTML = html
    return tmp.textContent || tmp.innerText || ''
  }
}

export function clearHtml (html) {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  let serialized = new XMLSerializer().serializeToString(doc)
  return serialized
    .replace(/^.*<body>(.+)<\/body>.*$/, '$1')
  //.replace(/(<img[^>]*>)/g,'<p>$1</p>')
}

export function cumulativeOffset (element) {
  let top = 0, left = 0
  do {
    top += (element?.offsetTop || 0) - (element?.scrollTop || 0)
    left += (element?.offsetLeft || 0) - (element?.scrollLeft || 0)
    element = element.offsetParent
  } while (element)
  return { top, left }
}

export function convertPlainToHTML (input_str) {
  let text_input //store input after beging trim()med
  let output_html = '' //store output
  let counter

  text_input = input_str.trim().replaceAll(/\r/g, '') //trim() input
  if (text_input.length > 0) {
    output_html += '<p>' //begin by creating paragraph
    for (counter = 0; counter < text_input.length; counter++) {
      switch (text_input[counter]) {
        case '\n':
          if (text_input[counter + 1] === '\n') {
            output_html += '<br /><br />'
            counter++
          } else output_html += '<br />'
          break

        case ' ':
          if (text_input[counter - 1] !== ' ' && text_input[counter - 1] !== '\t')
            output_html += ' '
          break

        case '\t':
          if (text_input[counter - 1] !== '\t')
            output_html += ' '
          break

        case '&':
          output_html += '&amp;'
          break

        case '"':
          output_html += '&quot;'
          break

        case '>':
          output_html += '&gt;'
          break

        case '<':
          output_html += '&lt;'
          break

        default:
          output_html += text_input[counter]

      }

    }
    output_html += '</p>' //finally close paragraph
  }
  return output_html // display output html
}

export function removeBodyBorders (text) {
  return text.replace(/<span class="bodyborder">--------<\/span>/g, '')
}

export function removeTagMark (text, bodyBorderShow = false) {
  text = bodyBorderShow ? text : removeBodyBorders(text)
  return text.replace(/<\/mark>/gm, '').replace(/<mark [^>]+>/gm, '')
}

export function formatText (root) {
  return removeTagMark(root).replaceAll('<p><br></p>', '<p></p>').replaceAll(/<br[^>]+data-mce-bogus[^>]+>/g, '').replace(/[\xAD]/g, '')
}

export function getStartIndex (bookmarkedContent, originalText, id) {
  const regex = new RegExp(`<span data-mce-type="bookmark" id="${id}_start" data-mce-style="overflow:hidden;line-height:0px" style="overflow: hidden; line-height: 0px;"></span>`)
  const originalTextWOTags = removeTagMark(originalText)
  let startIndex = bookmarkedContent.search(regex)
  const cuttedBookmarkedContent = bookmarkedContent.substring(0, startIndex)
  const cuttedTextArrLen = [...cuttedBookmarkedContent.matchAll(/<\/?[a-zA-Z]+[^>]*>/g)].length

  if (cuttedTextArrLen > 0) {
    let shift = 0
    const cuttedTextRealLen = cuttedBookmarkedContent.replaceAll(/<\/?[a-zA-Z]+[^>]*>/g, '').length
    const matchResult = [...originalTextWOTags.matchAll(/<\/?[a-zA-Z]+[^>]*>/g)]
    let counter = 0
    for (const match of matchResult) {
      shift += match[0].length
      counter++
      if (counter >= cuttedTextArrLen) {
        break
      }
    }
    startIndex = cuttedTextRealLen + shift
  }
  return startIndex
}

export function setLanguage (text) {
  const lngDetector = new LanguageDetect()
  lngDetector.setLanguageType('iso2')
  const detectedLanguage = lngDetector.detect(stripHtml(text))
  if (detectedLanguage && detectedLanguage[0] !== undefined && detectedLanguage[0][0] !== undefined) {
    const lang = convertLanguagesToFull(detectedLanguage[0][0])
    if (lang) {
      return lang
    }
  }
  return false
}

export function convertUTCDateToLocalDate (date) {
  if (typeof date === 'string') {
    return new Date(date.replace(/:UTC$/, '.0000Z'))
  }
  let newDate = new Date(date.getTime() + date.getTimezoneOffset() * 60 * 1000)
  let offset = date.getTimezoneOffset() / 60
  let hours = date.getHours()
  newDate.setHours(hours - offset)
  return newDate
}

export function convertLocalDateToUTCDate (date) {
  if (typeof date === 'string') {
    return new Date(date)
  }
  let newDate = new Date(date.getTime() + date.getTimezoneOffset() * 60 * 1000)
  let offset = date.getTimezoneOffset() / 60
  let hours = date.getHours()
  newDate.setHours(hours - offset)
  return newDate
}

export function printConvertedDate (date, lang) {
  return date.toLocaleDateString(convertLanguagesToFull(lang, true).toString(), {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    localeMatcher: 'best fit'
  }).replace(/\//g, '.').replace(/[a-z]/gi, '')
}

export function dateToSearchString (date, hours = 0) {
  const ret = date.toISOString()
  if (hours === 0)
    return ret.replace(/T/, ' ').replace(/\.000Z/, '')
  if (hours > 0)
    return ret.replace(/T\d{2}:\d{2}:\d{2}.000Z/, ' 23:59:59')
  return ret.replace(/T\d{2}:\d{2}:\d{2}.000Z/, ' 00:00:00')
}

export function dateToFullString (date) {
  const ret = date.toISOString()
  //:UTC  2025-01-17 11:09:40.244Z
  return ret.replace(/T/, ' ').replace(/\.\d{3}Z$/, ':UTC')
}

export function convertArrayToText (text, lang = 'de') {
  if (typeof text === 'string') {
    return text
  }
  switch (lang) {
    case 'en':
      return convertArrayToTextEN(text)
    case 'de':
    default:
      return convertArrayToTextDE(text)
  }
}

export function convertArrayToTextEN (text) {

  const oscillatingFirst = ['\'', '"', '‹', '‘', '»', '“']
  const oscillatingSecond = ['\'', '"', '›', '‘', '«', '”']
  let oscillatingCounter = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 }

  let newtext = text.map(val => val + ' ')

  newtext[newtext.length - 1] = newtext[newtext.length - 1]?.trimEnd()

  for (let i in text) {

    // No space BEFORE following characters: .,;!?:)]}…%‰
    // AND no space before "['’´](s|re|ve|d|m|em|ll)|n['’´]t"
    //  || (text.substring(i).match(/^[.,;!?:)\]}…%‰]+$/)
    if (text[i].match(/^[.,;!?:)\]}…%‰]+$/)
      || text[i].match(/^(['’´](s|re|ve|d|m|em|ll)|n['’´]t)$/)) {
      if (i > 0) {
        newtext[i - 1] = newtext[i - 1].trimEnd()
      }
    } else if (text[i].match(/^[(\[{„¿¡‚†‡]+$/)) {
      //No space AFTER following characters: ([{„“[¿¡{‚„†‡
      if (i < newtext.length - 1) {
        newtext[i] = newtext[i].trimEnd()
      }
    }

    //oscillating
    const firstFind = oscillatingFirst.indexOf(text[i])
    const secondFind = oscillatingSecond.indexOf(text[i])

    if (firstFind !== -1 && oscillatingCounter[firstFind] === 0) {
      newtext[i] = newtext[i].trimEnd()
      oscillatingCounter[firstFind] = 1
    } else if (secondFind !== -1 && oscillatingCounter[secondFind] === 1) {
      if (i > 0) {
        newtext[i - 1] = newtext[i - 1].trimEnd()
      }
      oscillatingCounter[secondFind] = 0
    }
  }
  return newtext.join('')
}

export function convertArrayToTextDE (text) {

  const oscillatingFirst = ['\'', '"', '‹', '‘', '»', '“']
  const oscillatingSecond = ['\'', '"', '›', '‘', '«', '”']
  let oscillatingCounter = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 }

  let newtext = text.map(val => val + ' ')

  newtext[newtext.length - 1] = newtext[newtext.length - 1]?.trimEnd()

  for (let i in text) {

    // No space BEFORE following characters: .,;!?:)]}…%‰
    if (text[i].match(/^[.,;!?:)\]}…%‰]+$/)) {
      if (i > 0) {
        newtext[i - 1] = newtext[i - 1].trimEnd()
      }
    } else if (text[i].match(/^[(\[{„¿¡‚†‡]+$/)) {
      //No space AFTER following characters: ([{„“[¿¡{‚„†‡
      if (i < newtext.length - 1) {
        newtext[i] = newtext[i].trimEnd()
      }
    }

    //oscillating
    const firstFind = oscillatingFirst.indexOf(text[i])
    const secondFind = oscillatingSecond.indexOf(text[i])

    if (firstFind !== -1 && oscillatingCounter[firstFind] === 0) {
      newtext[i] = newtext[i].trimEnd()
      oscillatingCounter[firstFind] = 1
    } else if (secondFind !== -1 && oscillatingCounter[secondFind] === 1) {
      if (i > 0) {
        newtext[i - 1] = newtext[i - 1].trimEnd()
      }
      oscillatingCounter[secondFind] = 0
    }
  }
  return newtext.join('')
}

export function checkTextSize (maxRequestSize, maxAnalizeTextSize, full, chars) {
  if (full > maxRequestSize) {
    return 'too big file size'
  }
  if (chars > maxAnalizeTextSize) {
    return 'text too long'
  }
  return ''
}

export function findToNotHTML (text, start, limit = 0, direction = -1) {
  let i = start
  while (text[i] && text[i]['text'].match(/<.+>/)) {
    if (i === limit) return limit
    i += direction
  }
  return i
}

export function getPositionNotHTML (text, start, limit = 1, direction = -1) {
  let i = start
  let counter = 0
  while (text[i]) {
    if (text[i]['text'].match(/<.+>/))
      continue
    counter++
    if (counter === limit) return i
    i += direction
  }
  return -1
}

export function md5Generator (inputString) {
  const hc = '0123456789abcdef'

  function rh (n) {
    let j, s = ''
    for (j = 0; j <= 3; j++) s += hc.charAt((n >> (j * 8 + 4)) & 0x0F) + hc.charAt((n >> (j * 8)) & 0x0F)
    return s
  }

  function ad (x, y) {
    let l = (x & 0xFFFF) + (y & 0xFFFF)
    let m = (x >> 16) + (y >> 16) + (l >> 16)
    return (m << 16) | (l & 0xFFFF)
  }

  function rl (n, c) {
    return (n << c) | (n >>> (32 - c))
  }

  function cm (q, a, b, x, s, t) {
    return ad(rl(ad(ad(a, q), ad(x, t)), s), b)
  }

  function ff (a, b, c, d, x, s, t) {
    return cm((b & c) | ((~b) & d), a, b, x, s, t)
  }

  function gg (a, b, c, d, x, s, t) {
    return cm((b & d) | (c & (~d)), a, b, x, s, t)
  }

  function hh (a, b, c, d, x, s, t) {
    return cm(b ^ c ^ d, a, b, x, s, t)
  }

  function ii (a, b, c, d, x, s, t) {
    return cm(c ^ (b | (~d)), a, b, x, s, t)
  }

  function sb (x) {
    let i
    let nblk = ((x.length + 8) >> 6) + 1
    let blks = new Array(nblk * 16)
    for (i = 0; i < nblk * 16; i++) blks[i] = 0
    for (i = 0; i < x.length; i++) blks[i >> 2] |= x.charCodeAt(i) << ((i % 4) * 8)
    blks[i >> 2] |= 0x80 << ((i % 4) * 8)
    blks[nblk * 16 - 2] = x.length * 8
    return blks
  }

  let i, x = sb(inputString), a = 1732584193, b = -271733879, c = -1732584194, d = 271733878, olda, oldb, oldc, oldd
  for (i = 0; i < x.length; i += 16) {
    olda = a
    oldb = b
    oldc = c
    oldd = d
    a = ff(a, b, c, d, x[i], 7, -680876936)
    d = ff(d, a, b, c, x[i + 1], 12, -389564586)
    c = ff(c, d, a, b, x[i + 2], 17, 606105819)
    b = ff(b, c, d, a, x[i + 3], 22, -1044525330)
    a = ff(a, b, c, d, x[i + 4], 7, -176418897)
    d = ff(d, a, b, c, x[i + 5], 12, 1200080426)
    c = ff(c, d, a, b, x[i + 6], 17, -1473231341)
    b = ff(b, c, d, a, x[i + 7], 22, -45705983)
    a = ff(a, b, c, d, x[i + 8], 7, 1770035416)
    d = ff(d, a, b, c, x[i + 9], 12, -1958414417)
    c = ff(c, d, a, b, x[i + 10], 17, -42063)
    b = ff(b, c, d, a, x[i + 11], 22, -1990404162)
    a = ff(a, b, c, d, x[i + 12], 7, 1804603682)
    d = ff(d, a, b, c, x[i + 13], 12, -40341101)
    c = ff(c, d, a, b, x[i + 14], 17, -1502002290)
    b = ff(b, c, d, a, x[i + 15], 22, 1236535329)
    a = gg(a, b, c, d, x[i + 1], 5, -165796510)
    d = gg(d, a, b, c, x[i + 6], 9, -1069501632)
    c = gg(c, d, a, b, x[i + 11], 14, 643717713)
    b = gg(b, c, d, a, x[i], 20, -373897302)
    a = gg(a, b, c, d, x[i + 5], 5, -701558691)
    d = gg(d, a, b, c, x[i + 10], 9, 38016083)
    c = gg(c, d, a, b, x[i + 15], 14, -660478335)
    b = gg(b, c, d, a, x[i + 4], 20, -405537848)
    a = gg(a, b, c, d, x[i + 9], 5, 568446438)
    d = gg(d, a, b, c, x[i + 14], 9, -1019803690)
    c = gg(c, d, a, b, x[i + 3], 14, -187363961)
    b = gg(b, c, d, a, x[i + 8], 20, 1163531501)
    a = gg(a, b, c, d, x[i + 13], 5, -1444681467)
    d = gg(d, a, b, c, x[i + 2], 9, -51403784)
    c = gg(c, d, a, b, x[i + 7], 14, 1735328473)
    b = gg(b, c, d, a, x[i + 12], 20, -1926607734)
    a = hh(a, b, c, d, x[i + 5], 4, -378558)
    d = hh(d, a, b, c, x[i + 8], 11, -2022574463)
    c = hh(c, d, a, b, x[i + 11], 16, 1839030562)
    b = hh(b, c, d, a, x[i + 14], 23, -35309556)
    a = hh(a, b, c, d, x[i + 1], 4, -1530992060)
    d = hh(d, a, b, c, x[i + 4], 11, 1272893353)
    c = hh(c, d, a, b, x[i + 7], 16, -155497632)
    b = hh(b, c, d, a, x[i + 10], 23, -1094730640)
    a = hh(a, b, c, d, x[i + 13], 4, 681279174)
    d = hh(d, a, b, c, x[i], 11, -358537222)
    c = hh(c, d, a, b, x[i + 3], 16, -722521979)
    b = hh(b, c, d, a, x[i + 6], 23, 76029189)
    a = hh(a, b, c, d, x[i + 9], 4, -640364487)
    d = hh(d, a, b, c, x[i + 12], 11, -421815835)
    c = hh(c, d, a, b, x[i + 15], 16, 530742520)
    b = hh(b, c, d, a, x[i + 2], 23, -995338651)
    a = ii(a, b, c, d, x[i], 6, -198630844)
    d = ii(d, a, b, c, x[i + 7], 10, 1126891415)
    c = ii(c, d, a, b, x[i + 14], 15, -1416354905)
    b = ii(b, c, d, a, x[i + 5], 21, -57434055)
    a = ii(a, b, c, d, x[i + 12], 6, 1700485571)
    d = ii(d, a, b, c, x[i + 3], 10, -1894986606)
    c = ii(c, d, a, b, x[i + 10], 15, -1051523)
    b = ii(b, c, d, a, x[i + 1], 21, -2054922799)
    a = ii(a, b, c, d, x[i + 8], 6, 1873313359)
    d = ii(d, a, b, c, x[i + 15], 10, -30611744)
    c = ii(c, d, a, b, x[i + 6], 15, -1560198380)
    b = ii(b, c, d, a, x[i + 13], 21, 1309151649)
    a = ii(a, b, c, d, x[i + 4], 6, -145523070)
    d = ii(d, a, b, c, x[i + 11], 10, -1120210379)
    c = ii(c, d, a, b, x[i + 2], 15, 718787259)
    b = ii(b, c, d, a, x[i + 9], 21, -343485551)
    a = ad(a, olda)
    b = ad(b, oldb)
    c = ad(c, oldc)
    d = ad(d, oldd)
  }
  return rh(a) + rh(b) + rh(c) + rh(d)
}

export function exportExcel (dataResult, result, percentResult) {

  // TO remove undefined value :
  dataResult.map(el => {
    Object.keys(el).map(element => {
      if (el[element] === undefined) {
        delete el[element]
      }
    })
  })

  let today = new Date()
  let date = today.getFullYear() + '-' + (today.getMonth() + 1) + '-' + today.getDate()
  if (dataResult?.length) {
    const ws = XLSX.utils.json_to_sheet(dataResult)
    const ws2 = XLSX.utils.json_to_sheet(percentResult)
    // const workSheet = XLSX.utils.json_to_sheet(dataResult);
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Trefferanzahl')
    XLSX.utils.book_append_sheet(wb, ws2, 'Prozentwerte')
    const bin = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
    downloadBufferAsFile(`${result}${date}`, bin)
  }
}

export function downloadBufferAsFile (filename, bin, textType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
  const element = document.createElement('a')
  const file = new Blob([bin], { type: textType })
  element.href = URL.createObjectURL(file)
  element.download = filename
  document.body.appendChild(element) // Required for this to work in FireFox
  element.click()
}

export function downloadTextAsFile (filename, text, textType = 'text/plain') {
  const pom = document.createElement('a')
  pom.setAttribute('href', 'data:' + textType + ';charset=utf-8,' + encodeURIComponent(text))
  pom.setAttribute('download', filename)

  if (document.createEvent) {
    const event = document.createEvent('MouseEvents')
    event.initEvent('click', true, true)
    pom.dispatchEvent(event)
  } else {
    pom.click()
  }
}

export function saveFileBlob (name, type, dataBlob) {
  const url = window.URL.createObjectURL(dataBlob)
  const a = document.createElement('a')
  a.setAttribute('href', url)
  a.setAttribute('download', name)
  a.click()
  window.URL.revokeObjectURL(url)
  a.remove()
}

export function uploadAsFile (filename, text, ext, title = '') {
  if (ext === 'pdf') {
    /** temporary disabled **/
    /*
    const doc = jsPDF('p', 'pt', 'a4');
    // We'll make our own renderer to skip this editor
    const options = {
        margin: 15,
        autoPaging:"slice",
        width: 600,
        windowWidth: 600,
        callback: function (pdf) {
            const iframe = document.createElement('iframe');
            iframe.setAttribute('style', 'position:absolute;right:0; top:0; bottom:0; height:100%; width:500px');
            document.body.appendChild(iframe);
            iframe.src = pdf.output('datauristring');
        }

    }
    doc.html(element, options).save('download.'+ext);
    */

  } else if (ext === 'html') {
    const header = `<html lang='de'><head><meta charset='utf-8'><title>${title}</title></head><body>`
    const footer = '</body></html>'
    const blob = new Blob(['\ufeff', header + text + footer], {
      type: 'text/html'
    })
    saveFileBlob('download.' + ext, 'text/html', blob)
  } else if (ext === 'txt') {
    const res = text.replaceAll(/<br\s*\/?>/g, '\n').replaceAll(/<\/[ph][\d]*>/g, '\n\n').replaceAll(/<[^>]+>/g, '')
    const blob = new Blob(['\ufeff', res], {
      type: 'text/plain'
    })
    saveFileBlob('download.' + ext, 'text/plain', blob)
  } else if (ext === 'doc') {
    const header = '<html lang=\'de\' xmlns:o=\'urn:schemas-microsoft-com:office:office\' xmlns:w=\'urn:schemas-microsoft-com:office:word\' xmlns=\'http://www.w3.org/TR/REC-html40\'>' +
      `<head><meta charset='utf-8'><title>${title}</title></head><body>`
    const footer = '</body></html>'
    const blob = new Blob(['\ufeff', header + text + footer], {
      type: 'application/msword'
    })
    saveFileBlob('download.' + ext, 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', blob)

  } else if (ext === 'docx') {
    const header = `<html lang='de'><head><meta charset='utf-8'><title>${title}</title></head><body>`
    const footer = '</body></html>'
    const res = text.replaceAll(/&nbsp;&nbsp;&nbsp;/g, '\t')
    asBlob(header + res + footer).then(res =>
      saveFileBlob('download.' + ext, 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', res))
  }

}

export function convertPipeToNewLine (text = '', html = false) {
  return html ? text.replace(/\|/g, '</br>') : text.replace(/\|/g, '\n')
}

export function filterByHIXCLIXFlesch (filter, clix, valueName) {
  if (filter['hix'] && filter['clix']) {
    return config.hixFleschFilter.indexOf(valueName) === -1 && (!clix || clix === 0)
  }
  if (filter['flesch'] && filter['clix']) {
    return config.hixFleschFilter.indexOf(valueName) === -1 && (!clix || clix === 0)
  }
  if (filter['hix'] && config.hixFleschFilter.indexOf(valueName) === -1) {
    return true
  }
  if (filter['flesch'] && config.hixFleschFilter.indexOf(valueName) === -1) {
    return true
  }
  return filter['clix'] && (!clix || clix === 0)
}

export function convertNumberToStringLocally (num, lang) {

  const convertedLanguage = convertLanguagesToFull(lang, true)
  //console.log('convertNumberToStringLocally', num, lang, typeof num, convertedLanguage )
  return convertedLanguage && typeof num === 'number' ?
    num.toLocaleString(convertedLanguage) : num
}

export function setAutoPosition (clientHeight, clientWidth, windowInnerHeight, windowInnerWidth, top, left) {
  let newY
  let newX
  if (clientHeight + top > windowInnerHeight - 80) {
    newY = top - clientHeight / 2 - 50
  } else {
    newY = top + clientHeight / 2
  }

  if (clientWidth + left > windowInnerWidth) {
    newX = left - clientWidth / 2
  } else {
    newX = left + 50
  }
  return [newX, newY]
}

export function countTermsWithPositions (value = [], type = 'terms') {
  if (!value || !value?.length)
    return 0
  if (type === 'terms')
    return value.reduce((acc, val) => acc + val?.position?.length, 0)
  if (type === 'array')
    return value.length
  return 0
}

export function textAddBodyBorders (text, resultData, offset = 0) {
  text = removeBodyBorders(text)

  if (resultData?.dataBodyRecognition) {
    const dataBodyRecognition = resultData.dataBodyRecognition
    if (dataBodyRecognition[1] > -1) {
      text = text.substring(0, dataBodyRecognition[1] + offset) + `<span class="bodyborder">--------</span>` + text.substring(dataBodyRecognition[1] + offset)
    }
    if (dataBodyRecognition[0] > -1) {
      text = text.substring(0, dataBodyRecognition[0]) + `<span class="bodyborder">--------</span>` + text.substring(dataBodyRecognition[0])
    }
  }
  return text
}

export function arrayAddBodyBorders (splittedText, resultData) {
  if (resultData?.dataBodyRecognition) {
    const dataBodyRecognition = resultData.dataBodyRecognition
    if (dataBodyRecognition[1] > -1) {
      splittedText[splittedText.length - 1].post = `<span class="bodyborder">--------</span>` + splittedText[splittedText.length - 1].post
    }
    if (dataBodyRecognition[0] > -1) {
      splittedText[0].pre += `<span class="bodyborder">--------</span>`
    }
  }
  return splittedText
}

export function prepareReplacement (replacement) {
  let returnReplacements = {
    false: {
      fullLength: replacement.length,
      meaning: false,
      replacements: []
    }
  }

  if (replacement.length > 0) {
    for (let index in replacement) {
      const { description, lemma, words, tag, settings } = replacement[index]
      const fullMeaning = settings?.meaning ? settings.meaning : []

      if (fullMeaning.length > 0) {
        for (const meaning of fullMeaning) {
          if (meaning && meaning.length > 0) {
            if (returnReplacements[JSON.stringify(meaning)] === undefined) {
              returnReplacements[JSON.stringify(meaning)] = {
                meaning: [convertArrayToText(meaning[0]), meaning[1] !== undefined ? meaning[1] : ''],
                replacements: []
              }
            }
            returnReplacements[JSON.stringify(meaning)].replacements.push({
              description,
              settings: settings,
              lemma,
              words,
              tag,
              index
            })
          }
        }
      } else {
        returnReplacements[false].replacements.push({
          description,
          settings: settings,
          lemma,
          words,
          tag,
          index
        })

      }
    }
  }
  return returnReplacements

}

export function recursiveAddFolder (returnArray, folder) {
  for (let i in returnArray) {
    if (returnArray[i].id === folder.parent) {
      returnArray[i].child.push({ ...folder, child: [] })
      return returnArray
    }
    if (returnArray[i]?.child?.length) {
      returnArray[i].child = recursiveAddFolder(returnArray[i].child, folder)
      return returnArray
    }
  }
  return returnArray
}

export function convertFoldersToArray (data) {
  let returnArray = []
  for (let folder of data) {
    if (folder.parent === 0) {
      returnArray.push({ ...folder, child: [] })
    } else {
      returnArray = recursiveAddFolder(returnArray, folder)
    }
  }
  return returnArray
}

export function readUploadedFileAsBinary (inputFile, isText = false) {
  const temporaryFileReader = new FileReader()

  return new Promise((resolve, reject) => {

    temporaryFileReader.onerror = () => {
      temporaryFileReader.abort()
      reject(new DOMException('Problem parsing input file.'))
    }

    temporaryFileReader.onload = () => {
      if (!isText) {
        resolve(temporaryFileReader.result)
        return
      }
      const result = new TextDecoder('utf8').decode(temporaryFileReader.result)
      for (const stringVal of result) {
        if (stringVal.charCodeAt(0) === 65533) {
          resolve(new TextDecoder('cp1252').decode(temporaryFileReader.result))
          return
        }
      }
      resolve(result)
    }
    temporaryFileReader.readAsArrayBuffer(inputFile)
  })
}

export function textAddDataIgnoredArray (splittedText, resultData, translate) {
  for (const ignore of resultData.dataIgnoredText) {
    const to = resultData.dataTokenizer.findIndex(v => v.begin >= ignore[0])
    if (to > -1) {
      if (to === 0) {
        splittedText[0]['pre'] = '<mark class="bgray" title="' + translate('ignore_hover_text') + '">' + splittedText[0]['pre'] + '</mark>'
      } else {
        const from = to - 1
        splittedText[from]['text'] = splittedText[from]['text'] + '<mark class="bgray" title="' + translate('ignore_hover_text') + '">'
        splittedText[to]['text'] = '</mark>' + splittedText[to]['text']
      }
    }
  }
  return splittedText
}

export function textClear (text) {
  return text.replace(/<\/p>$/, '').replaceAll(/<\/p>/g, '\n\n').replaceAll(/<\/h\d>/g, '\n\n').replaceAll(/<br *\/*>/g, '\n').replace(/<\/?[^>]+(>|$)/g, '')
}

export function checkAiText (text, maxChar, section) {
  const max = maxChar[section] !== undefined ? maxChar[section] : config.aiDefaultTextSize
  return textClear(text).length <= max
}

export const wordCount = (text) => {
  return textClear(text) ? textClear(text).split(/\s+/).length : 0
}

export const getChangedSettings = (current, initial) => {
  const changed = {}
  for (const key in current) {
    if (current[key] !== initial[key]) {
      changed[key] = current[key]
    }
  }
  return changed
}

export const generateReplacementValue = (setting, valuesFromState, i) => {
  switch (setting.type) {
    case 'select':
      const currentSetting = setting.values.find(value => value.id === valuesFromState[setting.name])
      if (!currentSetting) {
        return ''
      }
      if (typeof currentSetting.prompt === 'object') {
        if (currentSetting.prompt.length > i) {
          return currentSetting.prompt[i]
        } else {
          return ''
        }
      }
      return currentSetting?.prompt || ''
    case 'integer':
      if (typeof setting.prompt === 'object') {
        if (setting.prompt.length > i) {
          return setting.prompt[i].replace('{{number}}', String(valuesFromState[setting.name]))
        } else {
          return ''
        }
      }
      return setting.prompt.replace('{{number}}', String(valuesFromState[setting.name])) || ''
    default:
      if (typeof valuesFromState[setting.name] === 'object') {
        if (valuesFromState[setting.name].length > i) {
          return valuesFromState[setting.name][i]
        } else {
          return ''
        }
      }
      return valuesFromState[setting.name] || ''
  }
}

export async function getTreeTaggerResult (tlService, token, categoryLocaleName, element) {
  try {
    const replacementRes = await tlService.getTreeTager(token, categoryLocaleName, element)
    const word = []
    const lemma = []
    const tag = []
    if (replacementRes?.dataTokenizer) {
      for (const el of replacementRes.dataTokenizer) {
        word.push(el.word)
        lemma.push(el.lemma)
        tag.push(el.tag)
      }
      return { word, lemma, tag }
    }
    return { error: { message: '{"message":"empty dataTokenizer"}' } }
  } catch (e) {
    console.log(e)
    return { error: e }
  }
}

export function parseJwt (token) {
  const base64Url = token.split('.')[1]
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  const jsonPayload = decodeURIComponent(
    window.atob(base64).split('').map(
      function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
      }
    ).join('')
  )
  try {
    return JSON.parse(jsonPayload)
  } catch (e) {
    return { exp: '' }
  }
}

export function convertedValue (value, type, i18nLanguage, t, benchmarks = {}, row = {}, right, bgColors, colors) {
  switch (type) {
    case 'translated_string':
      return typeof value === 'string' ? t(value) : t(value.toString())
    case 'string':
      return typeof value === 'string' ? value : value.toString()
    case 'numeric':
      return value === undefined ? '-' : roundWithPrecision(value, 2)
    case 'date':
      return value ? printConvertedDate(convertUTCDateToLocalDate(value), i18nLanguage) : '-'
    case 'boolean':
      return <>
        <span className='sr-only'>{value ? t('True') : t('False')}</span>
        {value ? <FiberManualRecordRounded style={{ color: colors.colorProgress100 }} fontSize="small" /> :
          <FiberManualRecordRounded style={{ color: colors.colorOff }} fontSize="small" />}
      </>
    case 'benchmark':
      if (benchmarks[value] !== undefined) {
        return benchmarks[value].name[convertLanguagesToShort(i18nLanguage)]
      } else {
        return t('textype is not enabled')
      }
    case 'array':
      return value.length ? value.join(', ') : '-'
    case 'term_count':
      return <div className="d-flex align-items-center" style={{ marginLeft: '100px' }}>
        <NavLink to={`/administration/terminology/update/${row.id}`} className="mr-2">
          <Button className={`p-0`} style={{
            minWidth: 35, height: 35, backgroundColor: bgColors ? bgColors.bgBlue1000 : '',
            color: colors ? colors.colorContrast : ''
          }} size="small" variant="contained">
            <Menu/>
          </Button>
        </NavLink>
        {value}
      </div>
    case 'link':
      if (right && !row.user_deleted) {
        return <NavLink to={`/administration/user/update/${value}`} className="mr-2"
                        style={{
                          width: 120,
                          color: colors ? colors.colorBlue1000 : '',
                          cursor: 'pointer'
                        }}>{value}</NavLink>
      } else {
        return value
      }
    default:
      return value
  }
}

export const toastError = (message, bgColors, colors) => {
  toast.error(message, {
    style: {
      backgroundColor: bgColors.bgMuted,
      color: colors.colorText
    },
    closeButton: <Close style={{ color: colors.colorIcons }}/>,
    progressStyle: {
      backgroundColor: bgColors.bgDanger,
      color: colors.colorText
    },
    icon: <Error style={{ color: colors.colorStatusError }}/>
  })
}

export const toastSuccess = (message, bgColors, colors) => {
  toast.success(message, {
    style: {
      backgroundColor: bgColors.bgMuted,
      color: colors.colorText
    },
    progressStyle: {
      backgroundColor: bgColors.bgToastProgress,
    },
    closeButton: <Close style={{ color: colors.colorIcons }}/>,
    icon: <CheckCircleOutline style={{ color: colors.colorProgress100 }}/>
  })
}

export async function getTCLocaleName (tlService, token, id) {
  const filter = {
    return_values: JSON.stringify(['id', 'locale_name']),
    id
  }
  try {
    const respond = await tlService.getFilteredTermCategories(token, {}, filter)
    if (respond && respond?.data?.length) {
      return respond?.data[0]?.locale_name
    }
  } catch (err) {
    console.log(err)
  }
  return undefined
}

export async function checkSharedData (tlService, token, row, t, type = 'user') {
  let termCategories
  let textBins = {}
  let allowedLists = {}
  let usersInGroup = {}
  let groupsInCompany = {}
  let sharedArchivesInGroup = {}
  let benchmarks = {}
  let foldersGroup = [{ name: t('Default folder'), id: 0 }]
  let archiveFoundInFolderGroup = []
  let sharedArchivesInCompany = {}
  let foldersCompany = [{ name: t('Default folder'), id: 0 }]
  let archiveFoundInFolderCompany = []

  const isUser = type === 'user'
  const group_id = isUser ? row.user_group_id : row.id

  if (!(type === 'company')) {

    try {
      const res = await tlService.getFilteredArchiveFolders(token, {}, { user_group_id: group_id })
      if (res?.data?.length > 0) {
        res.data.forEach(folder => {
          foldersGroup.push({ name: folder.folder_name, id: folder.id })
        })
      }
    } catch (error) {
      console.log('getArchiveFolders', error)
    }

    try {
      sharedArchivesInGroup = await tlService.getFilteredArchive(token, {}, {
        user_group_id: group_id,
        extended_filter: isUser ? `[["&owner","=${row.id}"]]` : '',
        return_values: JSON.stringify(['folder_id', 'id'])
      })

      if (sharedArchivesInGroup?.data?.length > 0) {
        foldersGroup.forEach(folder => {
            const foundFolder = sharedArchivesInGroup.data.find(archive => archive.folder_id === folder.id)
            if (foundFolder) {
              archiveFoundInFolderGroup.push(folder.name)
            }
          }
        )
      }
    } catch (error) {
      console.log('getSharedArchives', error)
    }
  }

  if (!(type === 'group')) {

    try {
      const res = await tlService.getFilteredArchiveFolders(token, {}, { company_id: isUser ? row.company_id : row.id })
      if (res?.data?.length > 0) {
        res.data.forEach(folder => {
          foldersCompany.push({ name: folder.folder_name, id: folder.id })
        })
      }
    } catch (error) {
      console.log('getArchiveFolders', error)
    }

    try {
      sharedArchivesInCompany = await tlService.getFilteredArchive(token, {}, {
        company_id: isUser ? row.company_id : row.id,
        extended_filter: isUser ? `[["&owner","=${row.id}"]]` : '',
        return_values: JSON.stringify(['folder_id', 'id'])
      })

      if (sharedArchivesInCompany?.data?.length > 0) {
        foldersCompany.forEach(folder => {
            const foundFolder = sharedArchivesInCompany.data.find(archive => archive.folder_id === folder.id)
            if (foundFolder) {
              archiveFoundInFolderCompany.push(folder.name)
            }
          }
        )
      }
    } catch (error) {
      console.log('getSharedArchives', error)
    }

  }

  if (type === 'group') {
    try {
      usersInGroup = await tlService.getFilteredUsers(token, { limit: 1 }, {
        user_group_id: group_id,
        enabled: 1
      })
    } catch (error) {
      console.log('getGroupUsers', error)
    }
  }

  if (type === 'company') {
    try {
      groupsInCompany = await tlService.getFilteredGroups(token, { limit: 1 }, {
        company_id: group_id
      })
    } catch (error) {
      console.log('getCompanyGroups', error)

    }
  }

  try {
    let filterOptions = { return_values: JSON.stringify(['id']) }

    if (type === 'user') {
      filterOptions.user_id = row.id
    } else if (type === 'group') {
      filterOptions.user_group_id = group_id
    } else {
      filterOptions.company_id = group_id
    }

    termCategories = await tlService.getFilteredTermCategories(token, { limit: 1 }, filterOptions)
  } catch (error) {
    console.log('getTermCategories', error)
  }

  try {
    let filterOptions = { return_values: JSON.stringify(['id']) }
    if (type === 'user') {
      filterOptions.extended_filter = JSON.stringify([['&user_id', `=${row.id}`]])
    } else if (type === 'group') {
      filterOptions.extended_filter = JSON.stringify([['&user_group_id', `=${group_id}`]])
    } else {
      filterOptions.extended_filter = JSON.stringify([['&company_id', `=${group_id}`]])
    }
    textBins = await tlService.getFilteredTextbin(token, { limit: 1 }, filterOptions)

  } catch (error) {
    console.log('getTextBins', error)
  }

  try {
    let filterOptions = { return_values: JSON.stringify(['id']) }
    if (type === 'user') {
      filterOptions.extended_filter = JSON.stringify([['&user_id', `=${row.id}`]])
    } else if (type === 'group') {
      filterOptions.extended_filter = JSON.stringify([['&user_group_id', `=${group_id}`]])
    } else {
      filterOptions.extended_filter = JSON.stringify([['&company_id', `=${group_id}`]])
    }
    allowedLists = await tlService.getFilteredAllowedList(token, { limit: 1 }, filterOptions)
  } catch (error) {
    console.log('getAllowedLists', error)
  }

  try {
    let filterOptions = { return_values: JSON.stringify(['id']) }
    if (type === 'user') {
      filterOptions.extended_filter = JSON.stringify([['&user_id', `=${row.id}`]])
    } else if (type === 'group') {
      filterOptions.extended_filter = JSON.stringify([['&user_group_id', `=${group_id}`]])
    } else {
      filterOptions.extended_filter = JSON.stringify([['&company_id', `=${group_id}`]])
    }
    benchmarks = await tlService.getFilteredBenchmarks(token, { limit: 1 }, filterOptions)
  } catch (error) {
    console.log('getBenchmarks', error)
  }

  return {
    textBins,
    allowedLists,
    sharedArchivesInGroup,
    sharedArchivesInCompany,
    archiveFoundInFolderGroup,
    archiveFoundInFolderCompany,
    benchmarksInGroup: benchmarks,
    termCategories,
    usersInGroup,
    groupsInCompany
  }
}

export function areasWithoutRights (group_id, checks, user) {
  const prefix = 'admin_ugc_disabled-button-tooltip_missing-right'
  let areas = []
  for (const { fullcount, companyLevelRight, groupRight, area, companyOnly = false } of checks) {
    if (!fullcount && !checkUserRight(user, [companyLevelRight])) {
      if (companyOnly) {
        areas.push(`${prefix}_${area}`)
      } else {
        const hasRight = user?.user_rights?.find(
          (right) => right.user_group_id === group_id && right.right_id === groupRight
        )
        if (!hasRight) {
          areas.push(`${prefix}_${area}`)
        }
      }
    }
  }
  return areas
}

export function getColBasinLocaleName (lists, row, i18nLanguage) {
  const list = lists.find(l => l.id === row.term_category_id)
  return list ? convertLanguagesToShort(list.locale_name) : convertLanguagesToShort(i18nLanguage)
}

export function applyReplacements (innerReplacements, words, arrText) {
  const copyWords = [...words]
  for (let i = 0; i < innerReplacements.length; i++) {
    let repWord = ''
    if (arrText[innerReplacements[i][2]] && arrText[innerReplacements[i][3]]) {
      if (innerReplacements[i][2] === innerReplacements[i][3]) {
        repWord = arrText[innerReplacements[i][2]].substring(innerReplacements[i][0], innerReplacements[i][1] + 1)
      } else {
        repWord = arrText[innerReplacements[i][2]].substring(innerReplacements[i][0])
        repWord += arrText[innerReplacements[i][3]].substring(0, innerReplacements[i][1] + 1)
      }
      for (let j = 0; j < copyWords.length; j++) {
        copyWords[j] = copyWords[j].replaceAll(`$${i + 1}`, repWord)
      }
    }
  }
  return copyWords
}

export function manageNavigation (e, active, modalAccordionOpen) {

  if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
    e.preventDefault()
    if ((active) && active.getAttribute('data-accordion-item') && !e.altKey) {
      const accordionItems = document.querySelectorAll('[data-accordion-item]')
      const visibleAccordionItems = Array.from(accordionItems).filter(item => item.getAttribute('data-accordion-item') === 'accordion')
      const activeIndex = visibleAccordionItems.findIndex(item => item === active)
      const nextIndex = e.key === 'ArrowUp' ? activeIndex - 1 : activeIndex + 1

      if (visibleAccordionItems[nextIndex]) {
        visibleAccordionItems[nextIndex].focus()
      }

    } else if (active.tagName === 'P' && !!modalAccordionOpen && e.altKey) {
      const closeButtons = document.querySelectorAll('[data-accordion-arrow]')
      const filteredCloseButtons = Array.from(closeButtons).filter(button => button.getAttribute('data-accordion-arrow') === `${e.key === 'ArrowUp' ? 'up' : 'down'}${modalAccordionOpen}`)

      if (filteredCloseButtons.length > 0 && active.getAttribute('data-compare-item') === modalAccordionOpen) {
        filteredCloseButtons[0].click()
      }

    } else if ((active) && active.getAttribute('data-menu-item')) {
      const menuItems = document.querySelectorAll('[data-menu-item]')
      const visibleMenuItems = Array.from(menuItems).filter(item => item.tabIndex >= 0)
      const activeIndex = visibleMenuItems.findIndex(item => item === active)
      const nextIndex = e.key === 'ArrowUp' ? activeIndex - 1 : activeIndex + 1

      if (visibleMenuItems[nextIndex]) {
        visibleMenuItems[nextIndex].focus()
      }
    }

  } else if (e.key === 'Enter') {
    if (active.tagName === 'P') {
      const accordionItems = document.querySelectorAll('[data-accordion-item]')
      const visibleAccordionItems = Array.from(accordionItems).filter(item => item.getAttribute('data-accordion-item') === 'accordion')
      const activeIndex = visibleAccordionItems.findIndex(item => item === active)
      const activeItem = visibleAccordionItems[activeIndex]
      activeItem.click()
    } else if (!active.getAttribute('data-accordion-item') && !active.getAttribute('data-dialog-item')) {
      active.click()
    }
  } else if (e.key?.toLowerCase() === config.accessKeys.closeModal?.toLowerCase() && e.altKey) {
    const currentActiveElement = document.activeElement
    const isTooltipOpen = document.querySelector(`[accessKey="${config.accessKeys.toolTip}"]`)
    if (isTooltipOpen && (currentActiveElement === isTooltipOpen || isTooltipOpen.contains(currentActiveElement))) {
      const closeButton = document.querySelector('[data-close-modal]')
      if (closeButton) {
        closeButton.click()
      }
    }
  } else if (e.key === 'Escape') {
    const menuItems = document.querySelectorAll('[data-menu-item]')
    const visibleMenuItems = Array.from(menuItems).filter(item => item.tabIndex >= 0)
    const activeIndex = visibleMenuItems.findIndex(item => item === active)
    if (activeIndex > -1) {
      active.blur()
    }
  }
}

export function checkAccessKey (accessKey) {
  for (const { key, attribute } of config.keyAttributes) {
    if (accessKey === key) {
      const items = document.querySelectorAll(`[${attribute}]`)
      if (items.length > 0) {
        setTimeout(() => {
          items[0].focus()
        }, 100)
      }
    }
  }
}

export function generateFields (sections, formik, t, termCatOptions) {
  if (!sections) return {}
  let fieldId = 1
  return sections.reduce((acc, section) => {
    const sectionFields = section.values
      .filter(value => value.type !== 'category')
      .map((value) => {
        let controlType = 'text'
        let options = undefined
        let type
        let noLabel = false

        switch (value.type) {
          case 'boolean':
            controlType = 'switch'
            noLabel = true
            break
          case 'array':
            controlType = 'text'
            type = 'json'
            break
          case 'number':
            controlType = 'text'
            type = 'number'
            break
          case 'string':
            controlType = 'text'
            type = 'json'
            break
          case 'object':
          default:
            controlType = 'text'
        }

        return {
          id: fieldId++,
          control: controlType,
          name: `${section.key}${value.key}`,
          label: value.key,
          type,
          noLabel,
          ...(options ? { options } : {}),
        }
      })
    acc[`${section.key}Fields`] = sectionFields
    const otherFieldsFromFormik = Object.keys(formik.values).filter((val) => val.startsWith(section.key) && val.charAt(section.key.length) === val.charAt(section.key.length).toLowerCase() && !sectionFields.find((field) => field.name === val))
      .map((val) => {
          let controlType = 'text'
          let options = undefined
          let type
          let noLabel = false
          const label = val.slice(section.key.length)
          switch (typeof formik.values[val]) {
            case 'boolean':
              controlType = 'switch'
              noLabel = true
              break
            case 'number':
              controlType = 'text'
              type = 'number'
              break
            case 'string':
            case 'object':
              if (Array.isArray(formik.values[val])) {
                controlType = 'text'
                type = 'json'
                break
              }
              controlType = 'text'
              type = 'json'
              break
            default:
              controlType = 'text'
          }
          return {
            id: fieldId++,
            control: controlType,
            name: val,
            label: label,
            type,
            noLabel,
            ...(options ? { options } : {}),
          }
        }
      )
    acc[`${section.key}Fields`] = [...sectionFields, ...otherFieldsFromFormik]

    return acc
  }, {})
}

export function fillSettingsWithDefault (settings, settings_dir) {
  for (const [key, val] of Object.entries(settings)) {
    if (settings_dir[key]?.values) {
      for (const [settingKey, settingVal] of Object.entries(settings_dir[key].values)) {
        if (val.values[settingKey] === undefined && val.values['_list'] === undefined) {
          settings[key].values[settingKey] = settingVal[2]
        }
      }
    }
  }
  return settings
}

export function addReturnValues (settings, settings_dir) {
  let retValues = []
  for (const [key,] of Object.entries(settings)) {
    if (settings_dir[key]?.settings?.return_values
      && settings_dir[key]?.settings?.return_values?.length > 0) {
      settings_dir[key].settings.return_values.map(value => {
        if (retValues.indexOf(value) === -1) {
          retValues.push(value)
        }
        return null
      })
    }
  }
  return retValues
}

export function cleanSettings (settings) {
  const retSetting = {}
  for (const [key, val] of Object.entries(settings)) {
    retSetting[key] = { values: val.values }
  }
  return retSetting
}

export function getSettWOLists (settingPropertiesWOLists, values) {
  return settingPropertiesWOLists.reduce((acc, key) => {
    const filteredValues = Object.keys(values)
      .filter(val => val.startsWith(key))
      .reduce((acc, val) => {
        acc[val.slice(key.length)] = values[val]
        return acc
      }, {})

    if (Object.keys(filteredValues).length > 0) {
      acc[key] = { values: filteredValues }
    }

    return acc
  }, {})
}

export function getSettWithLists (settingPropertiesWithLists, values) {
  return settingPropertiesWithLists.reduce((acc, key) => {
    const filteredValues = Object.keys(values)
      .filter(val => val.startsWith(key))
      .reduce((acc, val) => {
        if (val.includes('_list_')) {
          const [listKey, listValue] = val.split('_list_')
          const [index, innerKey] = listValue.split('_')
          if (!acc['_list']) {
            acc['_list'] = {}
          }
          if (!acc['_list'][index]) {
            acc['_list'][index] = {}
          }
          acc['_list'][index][innerKey] = values[val]

        } else {
          acc[val.slice(key.length)] = values[val]
        }
        return acc
      }, {})

    if (Object.keys(filteredValues).length > 0) {
      acc[key] = { values: filteredValues }
    }

    return acc
  }, {})
}

export function generateNameDesc (fieldName, backendLanguages, values) {
  return Object.keys(backendLanguages).length > 0 ? Object.keys(backendLanguages).reduce((acc, key) => {
    const lang = key.split('_')[0]
    acc[lang] = values[`${fieldName}${key}`]
    return acc
  }, {}) : {}
}

export function flattenSettingsWithListValues (source, settingKeys, updateCallback) {
  for (const key of settingKeys) {
    const setting = source[key]
    if (!setting) continue

    const values = setting.values
    for (const newKey of Object.keys(values)) {
      if (newKey !== '_list') {
        updateCallback(`${key}${newKey}`, values[newKey])
      } else {
        for (const listKey of Object.keys(values)) {
          for (const innerKey of Object.keys(values[listKey])) {
            for (const [nestedInnerKey, nestedInnerValue] of Object.entries(values[listKey][innerKey])) {
              updateCallback(
                `${key}${newKey}_${innerKey}_${nestedInnerKey}`,
                nestedInnerValue
              )
            }
          }
        }
      }
    }
  }
}

export const handleAISelectionNavigation = (e, aiSelectWrapperRef, benchmarkSelectWrapperRef, bencmarkKey, typeKey
) => {
  const openSelect = (wrapperRef, key) => {
    if (!wrapperRef?.current || !key || typeof key !== 'string') return false
    if (e.altKey && e.key?.toLowerCase() === key.toLowerCase()) {
      e.preventDefault()
      const button = wrapperRef.current.querySelector('[role="button"]')
      if (button) {
        button.focus()
        const event = new KeyboardEvent('keydown', {
          key: 'ArrowDown',
          bubbles: true,
        })
        button.dispatchEvent(event)
      }
      return true
    }
    return false
  }

  if (!e) return

  if (openSelect(aiSelectWrapperRef, typeKey)) return
  if (openSelect(benchmarkSelectWrapperRef, bencmarkKey)) return
}

export function getOrderedFieldsToRender (fields, showMore, fieldSequence, renderFn) {
  const fieldsWithoutEnabled = fields.filter((field) => field.label !== 'enabled')

  const orderedFields = [...fieldsWithoutEnabled].sort((a, b) => {
    const aIndex = fieldSequence.sequence.indexOf(a.label)
    const bIndex = fieldSequence.sequence.indexOf(b.label)
    if (aIndex === -1 && bIndex === -1) return 0
    if (aIndex === -1) return 1
    if (bIndex === -1) return -1
    return aIndex - bIndex
  })

  if (!showMore) {
    const filtered = orderedFields.filter(f => fieldSequence.showFirst.includes(f.label))
    const toRender = filtered.length > 0 ? filtered : orderedFields
    return toRender.map(renderFn)
  }

  return orderedFields.map(renderFn)
}

export function handleKeyDown (e, focusedKey, setFocusedKey, list, handleOnChange, selectedRef, setOpenDrop, reverse = false) {
  const currentIndex = list.indexOf(focusedKey)
  if (e.key === 'ArrowUp') {
    const nextIndex = reverse ? (currentIndex - 1 + list.length) % list.length : (currentIndex + 1) % list.length
    setFocusedKey(list[nextIndex])
  } else if (e.key === 'ArrowDown') {
    const prevIndex = reverse ? (currentIndex + 1) % list.length : (currentIndex - 1 + list.length) % list.length
    setFocusedKey(list[prevIndex])
  } else if (e.key === 'Enter') {
    if (focusedKey) {
      handleOnChange(focusedKey)
      selectedRef.current = true
      setOpenDrop('')
    }
  }
}

export function contrastCalendar (bgColors, colors, createTheme) {
  return createTheme({
    palette: {
      primary: {
        main: bgColors.bgAiBtnHover,
      },
      background: {
        default: bgColors.bgPrimary0,
        paper: bgColors.bgPrimary0,
      },
      text: {
        primary: colors.colorText,
      },
    },
    overrides: {
      MuiPickersDay: {
        day: {
          color: colors.colorText,
        },
        daySelected: {
          backgroundColor: bgColors.bgAiBtnHover,
          color: colors.colorText,
        },
      },
      MuiPickersCalendarHeader: {
        dayLabel: {
          color: colors.colorText,
        },
        switchHeader: {
          color: colors.colorText,
        },
        iconButton: {
          color: colors.colorText,
        },
      },
      MuiButton: {
        textPrimary: {
          color: colors.colorText,
        },
      },
    },
  })
}

export function contrastCheckBox (colors, createTheme) {
  return createTheme({
    overrides: {
      MuiCheckbox: {
        root: {
          color: colors.colorMutedText,
          '&$checked': {
            color: `${colors.colorBlue1000} !important`,
          }
        },
      },
    },
  })
}

export const muiNoOutline = makeStyles({
  root: {
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '& .MuiFilledInput-underline:before': {
      borderBottom: 'none',
    },
    '& .MuiFilledInput-underline:after': {
      borderBottom: 'none',
    },
    '& .MuiFilledInput-underline:hover:before': {
      borderBottom: 'none',
    },
    '& .MuiFilledInput-root.Mui-focused': {
      boxShadow: 'none',
    },
  },
})

export const switchStyles = (bgColors, colors) => makeStyles({
  root: {
    '& .MuiSwitch-switchBase': {
      color: bgColors.bgSwitchOff
    },
    '& .MuiSwitch-switchBase.Mui-checked': {
      color: colors.colorOn
    },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
      backgroundColor: bgColors.bgSwitchOn
    },
    '& .MuiSwitch-track': {
      backgroundColor: bgColors.bgSwitchOff
    },
    '& .MuiSwitch-switchBase.Mui-disabled': {
      color: colors.colorMutedText
    },
    '& .MuiSwitch-switchBase.Mui-checked.Mui-disabled': {
      color: colors.colorMutedText
    },
    '& .MuiSwitch-track.Mui-disabled': {
      backgroundColor: bgColors.bgMuted,
    },
  },
  label: {
    color: colors.colorText,
    '&.Mui-disabled': {
      color: colors.colorMutedText
    },
  },
})

export const checkBoxStyles = (colors) =>
  makeStyles({
    checkbox: {
      '& svg': {
        color: colors.colorIcons
      },
      '&.Mui-checked svg': {
        color: colors.colorOn
      },
    }
  })

export const progressBarStyles = (bgColors, colors) =>
  makeStyles({
    root: {
      backgroundColor: bgColors.bgPrimary200,
    },
    bar: {
      backgroundColor: colors.colorText
    },
  })

export const useAutoCompleteStyles = (bgColors, colors) =>
  makeStyles({
    paper: {
      backgroundColor: bgColors.bgPrimary200,
      color: colors.colorText,
    },
    option: {
      '&[data-focus="true"]': {
        backgroundColor: bgColors.bgPrimary400,
      },
      '&[aria-selected="true"]': {
        backgroundColor: bgColors.bgPrimary600,
        color: colors.colorText,
      },
      '&.Mui-selected': {
        backgroundColor: bgColors.bgPrimary600,
        color: colors.colorText,
      },
      '&.Mui-selected:hover': {
        backgroundColor: bgColors.bgPrimary600,
        color: colors.colorText,
      },
    },
    clearIndicator: {
      color: colors.colorIcons,
      '&:hover': {
        backgroundColor: bgColors.bgPrimary400,
        color: colors.colorText,
      },
    },
    keyboardButton: {
      color: colors.colorText,
    },
    noOptions: {
      color: colors.colorIcons
    },
    popupIndicator: {
      color: colors.colorIcons,
      '&:hover': {
        backgroundColor: bgColors.bgPrimary400,
        color: colors.colorText,
      },
    },
    inputRoot: {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'transparent',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: 'transparent',
      },
      '&.Mui-focused': {
        boxShadow: 'none',
      },
    },
    outlinedRoot: {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'transparent'
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: colors.colorText,
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: colors.colorText,
      },
    },
    filledRoot: {
      backgroundColor: bgColors.bgPrimary200,
      color: colors.colorText,
      '& .MuiFilledInput-input': {
        color: colors.colorText,
      },
      '&.Mui-focused': {
        boxShadow: 'none',
      },
    },
    filledNoUnderline: {
      '&:before, &:after': {
        borderBottom: 'none !important',
      },
      '&:hover:before': {
        borderBottom: 'none !important',
      },
    },
  })
  
export const useAuthInputStyles = makeStyles({
  autofillFix: props => ({
    '&:-webkit-autofill': {
      WebkitTextFillColor: props.textColor,
      boxShadow: `0 0 0 1000px ${props.bgMuted} inset`,
      WebkitBoxShadow: `0 0 0 1000px ${props.bgMuted} inset`,
      caretColor: props.textColor,
    },
  }),
})

export function handleDropClick (type, setOpenDrop, selectedRef) {
  if (selectedRef.current) {
    selectedRef.current = false
    return
  }
  setOpenDrop(s => s !== type ? type : '')
}

export function fixUmlauts (text) {
  const decomposedUmlautRegex = /([aeiouAEIOUy])\u0308/g
  const umlautTable = {
    'a': 'ä',
    'e': 'ë',
    'i': 'ï',
    'o': 'ö',
    'u': 'ü',
    'y': 'ÿ',
    'A': 'Ä',
    'E': 'Ë',
    'I': 'Ï',
    'O': 'Ö',
    'U': 'Ü',
    'Y': 'Ÿ'
  }
  return text.replaceAll(decomposedUmlautRegex, function (match, capture) {
    return umlautTable[capture] || match
  })
}

export function generateBList (filteredTaskTypes, benchmarks, useBenchmark, benchmarkSettingName) {

  const bList = []
  const typesList = new Set()
  let selectedBList = -1

  for (const benchmark of Object.values(benchmarks)) {
    const localTypesList = new Set()
    if (benchmark?.settings?.[benchmarkSettingName]?.values?.enabled) {
      const lstBTasks = benchmark?.settings?.[benchmarkSettingName]?.values?.taskId
      if (lstBTasks?.length) {
        const arrBTasks = lstBTasks.split(',')
        for (let i = 0; i < arrBTasks.length; i++) {
          const tmpTypeId = parseInt(arrBTasks[i].trim(), 10)
          const tmpType = filteredTaskTypes.filter(el => el?.id === tmpTypeId)

          // if type defined  in a benchmark
          if (!isObjectEmpty(tmpType) && !localTypesList.has(tmpTypeId)) {
            bList.push({
              id: benchmark.id,
              typeId: tmpTypeId,
              name: benchmark.name,
              taskName: tmpType[0]?.shared_settings?.displayName
            })

            // update default if exist
            if (useBenchmark === benchmark.id && selectedBList === -1) {
              selectedBList = bList.length - 1
            }
            typesList.add(tmpTypeId)
            localTypesList.add(tmpTypeId)
          }
        }
      }
    }
  }

  if (selectedBList === -1 && bList?.length) {
    selectedBList = 0
  }

  return [selectedBList, bList]

}

export function handlePromptValueToggle (prevValues, settingName, valueId) {
const currentValue = prevValues[settingName]
  if (currentValue === valueId) {
    const updated = { ...prevValues }
    delete updated[settingName]
    return updated
  }
  return { ...prevValues, [settingName]: valueId }
}

export {
  compose,
  mammothAsync, readFileAsync, sleep,
  appendFilters, appendLimits, getResource
}
